import {type FormEvent, useEffect, useId, useRef, useState} from 'react';
import {AxiosError} from 'axios';

import {Alert, Button, FieldError, Input, Label} from '@design-system';

import useListInvitations from '../../Queries/useListInvitations';
import useCreateInvitation from '../../Queries/useCreateInvitation';
import type {Invitation} from '../../Types/invitationTypes';
import useRetryFocus from '../../Util/useRetryFocus';

const LIST_ERROR_COPY = "We couldn't load your invitations. Please try again.";
const LIST_LOADING_COPY = 'Loading invitations…';
const LIST_EMPTY_COPY = 'No invitations sent yet.';
const SUCCESS_COPY = 'Invitation sent!';
const CREATE_ERROR_COPY = "We couldn't send the invitation. Please try again.";
const CREATE_LIMIT_COPY =
  "We couldn't send the invitation because you have reached the limit for now. Please try again later.";
const SUBMIT_LABEL = 'Send Invite';
const SUBMIT_LABEL_PENDING = 'Sending…';
const RETRY_LABEL = 'Retry';
// The Alert's floor for a confirmation cleared on a timer.
const SUCCESS_TIMEOUT_MS = 5000;

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * The refusal of the address itself: the message CreateInvitationRequest (or
 * the duplicate check) writes for a person under `errors.invitee_email`, sent
 * with a 422. It names what to change, so it is shown under the field.
 */
function getInviteeEmailError(error: AxiosError): string {
  if (error.response?.status !== 422) return '';
  const errors = (error.response.data as {errors?: unknown} | undefined)?.errors;
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) return '';
  const inviteeEmail = (errors as {invitee_email?: unknown}).invitee_email;
  return Array.isArray(inviteeEmail) && typeof inviteeEmail[0] === 'string' ? inviteeEmail[0] : '';
}

/**
 * Every other failure is told in our words. The server's `message` is its own
 * wording ("This action is unauthorized.", or "Server Error" from a production
 * 500), and axios sets `error.message` to text such as "Request failed with
 * status code 500". The rate limit is the one refusal a person can do something
 * about, as in the share dialog.
 */
function getCreateErrorMessage(error: AxiosError): string {
  return error.response?.status === 429 ? CREATE_LIMIT_COPY : CREATE_ERROR_COPY;
}

const cellStyles = 'border-b border-(--compass-border) px-3 py-2';
const headerStyles = `${cellStyles} font-medium`;

function StatusText({status}: {status: Invitation['status']}) {
  // Colour is decorative: the text alone conveys the state. Both role tokens
  // resolve to values with sufficient contrast on `--compass-surface` in both
  // themes, and no palette utility is used.
  return (
    <span
      className={
        status === 'accepted'
          ? 'font-medium text-(--compass-primary)'
          : 'text-(--compass-on-surface)'
      }>
      {status === 'accepted' ? 'Accepted' : 'Pending'}
    </span>
  );
}

/**
 * The Professional /my-account invitations panel. COM-307 Phase 2.
 *
 * Owns the list and create flow only; the two queries stay unchanged. The
 * failure branch is ordered before the rows so a refetch that fails with
 * cached data hides the stale rows behind the alert; a local `isRetrying`
 * flag latches that alert across React Query 4's no-data reducer reset, so
 * Retry never unmounts and drops focus to the document body while it is in
 * flight. A ref re-entry guard blocks a second refetch while one is in
 * flight even when the query has data (RQ4 would otherwise cancel and
 * restart it under `cancelRefetch: true`). The submit is guarded the same
 * way and stays focusable while pending: a disabled focused button would
 * drop focus to `<body>` at the moment the request settles.
 *
 * The list's failure takes focus when it first shows only if nothing else has
 * it: it must not pull the person from a field they are filling in. Retry keeps focus
 * through its request and a failure that follows, as Send Invite does through
 * a send; a send's failure is announced and describes the field.
 *
 * How often a failed list read is asked again is the application client's:
 * the Professional client does not retry a 403, so a refused list shows its
 * alert after one request (COM-427).
 */
export default function InvitationSection() {
  const inputId = useId();
  const errorMessageId = `${inputId}-error`;
  const headingId = `${inputId}-heading`;

  const [email, setEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);

  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryGuardRef = useRef(false);
  const submitGuardRef = useRef(false);

  const {data, isLoading, error: listError, refetch} = useListInvitations();
  const createMutation = useCreateInvitation();

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const invitations = data?.invitations ?? [];

  // The ProHome-style latch: `error` clears mid-refetch on a query with no
  // cached data, so anchoring on `isRetrying` too keeps the alert and Retry
  // mounted across that reducer reset.
  const showListFailure = Boolean(listError) || isRetrying;
  const showListLoading = isLoading && !showListFailure;
  // Retry is pressed directly, not through the hook's `retry`: the failure stays
  // on screen throughout, so only its first appearance can move focus.
  const {failureRef: listFailureRef} = useRetryFocus(
    {failed: showListFailure, focusFirstFailure: 'when-unfocused'},
    () => undefined
  );

  const handleRetry = () => {
    if (retryGuardRef.current) return;
    retryGuardRef.current = true;
    setIsRetrying(true);
    // React Query's refetch resolves (never rejects) on failure, so a plain
    // finally is enough to clear both the guard and the retrying flag.
    void refetch().finally(() => {
      retryGuardRef.current = false;
      setIsRetrying(false);
    });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (submitGuardRef.current) return;
    submitGuardRef.current = true;
    setSuccessMessage('');
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    createMutation.mutate(
      {invitee_email: email.trim()},
      {
        onSuccess: () => {
          setEmail('');
          setSuccessMessage(SUCCESS_COPY);
          successTimeoutRef.current = setTimeout(() => {
            setSuccessMessage('');
            successTimeoutRef.current = null;
          }, SUCCESS_TIMEOUT_MS);
        },
        onSettled: () => {
          submitGuardRef.current = false;
        },
      }
    );
  };

  const createError = createMutation.isError ? createMutation.error : null;
  // An address the server refused is the field's own error; anything else is
  // the form's, and leaves the address valid.
  const inviteeEmailError = createError ? getInviteeEmailError(createError) : '';
  const createErrorMessage = createError
    ? inviteeEmailError || getCreateErrorMessage(createError)
    : '';
  // Either way the message describes the field it is about.
  const inputDescribedBy = createErrorMessage ? errorMessageId : undefined;
  const inputIsInvalid = Boolean(inviteeEmailError);

  return (
    <section data-testid="invitation-section">
      <h2 id={headingId} className="mb-2 text-2xl font-bold">
        Invitations
      </h2>
      <p className="mb-4 text-sm text-(--compass-on-surface)">
        Invite someone to join Compass by entering their email address.
      </p>

      <form onSubmit={handleSubmit} className="mt-4">
        <Label htmlFor={inputId} className="mb-1 block">
          Email address
        </Label>
        <div className="mt-1 flex flex-wrap items-start gap-2">
          <Input
            id={inputId}
            type="email"
            required
            placeholder="colleague@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={inputIsInvalid ? true : undefined}
            aria-describedby={inputDescribedBy}
            className="h-11 w-full max-w-md sm:flex-1"
            data-testid="invitation-email"
          />
          <Button
            type="submit"
            variant="default"
            className="min-h-11"
            aria-busy={createMutation.isPending ? true : undefined}
            data-testid="invitation-submit">
            {createMutation.isPending ? SUBMIT_LABEL_PENDING : SUBMIT_LABEL}
          </Button>
        </div>
        {inviteeEmailError ? (
          <FieldError id={errorMessageId} role="alert" data-testid="invitation-create-error">
            {inviteeEmailError}
          </FieldError>
        ) : createErrorMessage ? (
          <Alert variant="error" id={errorMessageId} data-testid="invitation-create-error">
            {createErrorMessage}
          </Alert>
        ) : null}
        {/* Mounted throughout, so the confirmation is announced when it arrives. */}
        <Alert variant="success" data-testid="invitation-create-status">
          {successMessage}
        </Alert>
      </form>

      <hr className="my-6 border-t border-(--compass-border)" />

      {showListFailure ? (
        <Alert
          ref={listFailureRef}
          variant="error"
          data-testid="invitations-error"
          action={
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              data-testid="invitations-retry"
              aria-busy={isRetrying ? true : undefined}
              onClick={handleRetry}>
              {RETRY_LABEL}
            </Button>
          }>
          {LIST_ERROR_COPY}
        </Alert>
      ) : showListLoading ? (
        <p
          role="status"
          data-testid="invitations-status"
          className="text-sm text-(--compass-on-surface)">
          {LIST_LOADING_COPY}
        </p>
      ) : invitations.length === 0 ? (
        // An outcome of the load, so a polite info status.
        <Alert variant="info" data-testid="invitations-empty">
          {LIST_EMPTY_COPY}
        </Alert>
      ) : (
        <div className="overflow-x-auto" data-testid="invitations-table-wrap">
          <table
            aria-labelledby={headingId}
            className="min-w-full text-left text-sm text-(--compass-on-surface)">
            <thead>
              <tr>
                <th scope="col" className={headerStyles}>
                  Email
                </th>
                <th scope="col" className={headerStyles}>
                  Status
                </th>
                <th scope="col" className={headerStyles}>
                  Sent
                </th>
                <th scope="col" className={headerStyles}>
                  Accepted
                </th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((invitation) => (
                <tr key={invitation.id}>
                  <td className={cellStyles}>{invitation.invitee_email}</td>
                  <td className={cellStyles}>
                    <StatusText status={invitation.status} />
                  </td>
                  <td className={cellStyles}>{formatDate(invitation.created_at)}</td>
                  <td className={cellStyles}>{formatDate(invitation.accepted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
